/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import { Grid, makeStyles, Typography } from '@material-ui/core';
import { Formik } from 'formik';
import i18n from 'i18n';
import {
  GetOperatorFields,
  PasswordResetModalProps,
} from 'modules/operators/interfaces';
import GenericForm from 'skyone-lib/lib/GenericForm';
import { applyI18n } from 'utils/helpers';
import Button from 'components/Button';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { editOperatorRequest } from 'modules/operators/actions';
import validationSchema from '../../schema';
import { resetFields } from '../../formFields';
import styles from './styles';

const useStyles = makeStyles(styles);

const initialValues: PasswordResetModalProps = {
  password: '',
  password_confirmation: '',
  current_password: '',
};

interface ManualResetModalProps {
  operator: GetOperatorFields;
  isLoading: boolean;
  onCancel: () => void;
  editOperatorRequest: (props: { values: any }) => void;
}

const ManualResetModalProps = ({
  operator,
  isLoading,
  onCancel,
  editOperatorRequest,
}: ManualResetModalProps) => {
  const classes = useStyles();
  const [formValues, setFormValues] = React.useState(initialValues);
  const [restrictionsMap, setRestrictionsMap] = React.useState<
    Record<string, boolean>
  >({});

  const checkRestrictions = (password: string) => {
    setRestrictionsMap({
      REQUIRE_UPPERCASE: !!password.match(/[A-Z]/g),
      REQUIRE_LOWERCASE: !!password.match(/[a-z]/g),
      REQUIRE_NUMBER: !!password.match(/\d/g),
      REQUIRE_SYMBOL: !!password.match(
        /[!@#$%¨&*()_+{}¹²³£¢¬§`´~^[\]:;,.<>ªº°?/\\|\-"'=]/g
      ),
      REQUIRE_MIN_LENGTH: password.trim().length >= 8,
      REQUIRE_MAX_LENGTH: password.trim().length <= 32,
    });
  };

  const handleSubmit = (values: PasswordResetModalProps) => {
    editOperatorRequest({
      values: {
        ...operator,
        company_uuid: operator?.company?.company_uuid,
        password: values.password,
        current_password: values.current_password,
      },
    });
  };

  const errors = React.useMemo(() => {
    let password_confirmation;
    if (formValues.password !== formValues.password_confirmation) {
      password_confirmation = i18n.t('PASSWORD_NOT_MATCH_KEY');
    } else if (formValues.password_confirmation === '') {
      password_confirmation = i18n.t('REQUIRED_FIELD_KEY');
    }
    const password =
      formValues.password === '' ? i18n.t('REQUIRED_FIELD_KEY') : undefined;
    const current_password =
      formValues.current_password === ''
        ? i18n.t('REQUIRED_FIELD_KEY')
        : undefined;
    return { password, password_confirmation, current_password };
  }, [formValues]);

  return (
    <Grid container className={classes.container}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema.reset}
        onSubmit={handleSubmit}
        validateOnBlur
      >
        {({ setFieldValue }) => (
          <Grid container xs={12} spacing={2}>
            <GenericForm
              fields={formValues}
              screenFields={applyI18n(resetFields.slice(0, 2), i18n.language)}
              changeField={({ key, value }) => {
                if (key === 'password') {
                  checkRestrictions(value?.toString()!);
                }
                setFormValues({ ...formValues, [key]: value });
              }}
              setFieldValue={setFieldValue}
              errors={errors}
            />
            <Grid item xs={12}>
              <Typography className={classes.passwordRules}>
                {i18n.t('PASSWORD_MUST_CONTAIN_KEY')}:
              </Typography>
              <Grid container>
                <Grid>
                  <ul className={classes.listRestrictions}>
                    <li
                      className={
                        restrictionsMap.REQUIRE_UPPERCASE ? 'valid' : ''
                      }
                    >
                      {i18n.t('REQUIRED_LETTER_UPPERCASE_KEY')}
                    </li>
                    <li
                      className={
                        restrictionsMap.REQUIRE_LOWERCASE ? 'valid' : ''
                      }
                    >
                      {i18n.t('REQUIRED_LETTER_LOWERCASE_KEY')}
                    </li>
                    <li
                      className={restrictionsMap.REQUIRE_NUMBER ? 'valid' : ''}
                    >
                      {i18n.t('REQUIRED_LETTER_TO_NUMBER_KEY')}
                    </li>
                  </ul>
                </Grid>
                <Grid>
                  <ul className={classes.listRestrictions}>
                    <li
                      className={restrictionsMap.REQUIRE_SYMBOL ? 'valid' : ''}
                    >
                      {i18n.t('REQUIRED_LETTER_SPECIAL_CHARACTER_KEY')}
                    </li>
                    <li
                      className={
                        restrictionsMap.REQUIRE_MIN_LENGTH ? 'valid' : ''
                      }
                    >
                      {i18n.t('REQUIRE_MIN_LENGTH_TEXT_KEY', {
                        min_length: 8,
                      })}
                    </li>
                    <li
                      className={
                        restrictionsMap.REQUIRE_MAX_LENGTH ? 'valid' : ''
                      }
                    >
                      {i18n.t('REQUIRE_MAX_LENGTH_TEXT_KEY', {
                        max_length: 32,
                      })}
                    </li>
                  </ul>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} style={{ marginTop: 16 }}>
              <Typography variant="h6">
                {i18n.t('ENTER_YOUR_PASSWORD_BELOW_KEY')}:
              </Typography>
            </Grid>
            <GenericForm
              fields={formValues}
              screenFields={applyI18n(resetFields.slice(2, 3), i18n.language)}
              changeField={({ key, value }) => {
                setFormValues({ ...formValues, [key]: value });
              }}
              setFieldValue={setFieldValue}
              errors={errors}
            />

            <Grid container justify="flex-end" className={classes.buttonsBase}>
              <Grid container className={classes.buttonsAlign}>
                <Button
                  variant="outlined"
                  onClick={() => handleSubmit(formValues)}
                  isLoading={isLoading}
                  submitButton
                  disabled={
                    Object.values(errors).findIndex(
                      value => value?.length >= 0
                    ) !== -1
                  }
                >
                  {i18n.t('SAVE_KEY')}
                </Button>
                <Button
                  id="cancel-button"
                  variant="outlined"
                  onClick={onCancel}
                >
                  {i18n.t('CANCEL_KEY')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Formik>
    </Grid>
  );
};

const mapStateToProps = ({ operators: { isLoading } }: ApplicationState) => ({
  isLoading,
});

export default connect(mapStateToProps, { editOperatorRequest })(
  ManualResetModalProps
);
